import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { IAgreementPreviewResponse } from "@bca-agreement/models/agreement-preview-response";
import { IIssuerResponse } from "@bca-agreement/models/issuer-response";
import { ICompanyCheckResponse } from "@bca-company-data/models/company-check-response";
import { ICompanyProfileResponse } from "@bca-company-data/models/company-profile-response";
import { ICompanyResponse } from "@bca-company-data/models/company-response";
import { ISignatoryResponse } from "@bca-company-data/models/signatory-status-response";
import { IProductResponse } from "@bca-compose/models/product-response";
import { IRouteResponse } from "@bca-compose/models/route-response";
import { IStation } from "@bca-compose/models/station";
import { PurchaseEventRequest } from "@bca-confirmation/models/purchase-event-request";
import { IStartPaymentResponse } from "@bca-confirmation/models/start-payment-response";
import { IStatusPaymentResponse } from "@bca-confirmation/models/status-payment-response";
import * as fromReceipt from "@bca-receipt/store/receipt.reducer";
import { TravelerClass } from "@bca-shared/models/_enums";
import { ISearchableAddress } from "@bca-shared/models/address";
import { ICompanySignatory } from "@bca-shared/models/company-signatory";
import { APP_CONFIG, IAppConfiguration } from "@bca-shared/models/config";
import { IAddressResponse } from "@bca-user-data/models/address-response";
import { IImageValidationResponse } from "@bca-user-data/models/image-validation-response";
import { IMiddleNamesResponse } from "@bca-user-data/models/middlenames-reponse";
import { IActionCodeResponse } from "@bca-verify/models/action-code-response";
import { environment } from "@environments/environment";
import { TranslateService } from "@ngx-translate/core";
import { Observable } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class ApiService {
  constructor(
    private _http: HttpClient,
    @Inject(APP_CONFIG) private _config: IAppConfiguration,
    private _translate: TranslateService,
  ) {}

  public searchCompany(searchTerm: string): Observable<ICompanyResponse> {
    const { api_url_bca, api_version } = this._config;
    return this._http.get<ICompanyResponse>(
      `${api_url_bca}/kvk-${api_version}/bedrijven?input=${searchTerm}`,
    );
  }

  public validateSignatory(
    companySignatory: ICompanySignatory,
  ): Observable<ISignatoryResponse> {
    const { api_url_bca, api_version } = this._config;
    const url = `${api_url_bca}/kvk-${api_version}/tekenbevoegde?kvkNumber=${companySignatory.kvkNumber}`;
    const requestBody = {
      credentials: companySignatory.initials,
      prefix: companySignatory.prefix,
      lastName: companySignatory.lastName,
    };

    return this._http.post<ISignatoryResponse>(url, requestBody);
  }

  public searchStation(searchTerm: string): Observable<IStation[]> {
    const { api_url_rio, api_key_rio } = this._config;
    return this._http.get<IStation[]>(
      `${api_url_rio}/stations?q=${searchTerm}&countryCode=nl`,
      {
        headers: {
          "Ocp-Apim-Subscription-Key": api_key_rio,
        },
      },
    );
  }

  public getRoutePrices(
    from: IStation,
    to: IStation,
    clazz: TravelerClass,
  ): Observable<IRouteResponse> {
    const { api_url_bca, api_version } = this._config;
    return this._http.get<IRouteResponse>(
      `${api_url_bca}/producten-${api_version}/trajectprice?from=${from.id}&to=${
        to.id
      }&class=${clazz}`,
    );
  }

  public getProducts(): Observable<IProductResponse> {
    const { api_url_bca, api_version } = this._config;
    return this._http.get<IProductResponse>(
      `${api_url_bca}/producten-${api_version}/producten`,
    );
  }

  public startPayment(
    receipt: fromReceipt.IState,
  ): Observable<IStartPaymentResponse> {
    const { api_url_bca, api_version } = this._config;
    const language = this._translate.currentLang;
    return this._http.post<IStartPaymentResponse>(
      `${api_url_bca}/ideal-${api_version}/start`,
      {
        ...receipt,
        language,
      },
    );
  }

  public statusPayment(
    transactionId: string,
  ): Observable<IStatusPaymentResponse> {
    const { api_url_bca, api_version } = this._config;
    return this._http.post<IStatusPaymentResponse>(
      `${api_url_bca}/ideal-${api_version}/status`,
      transactionId,
    );
  }

  public sendAvoPurchaseEvent(event: PurchaseEventRequest): Observable<any> {
    const { api_url_bca, api_version } = this._config;
    return this._http.post<any>(`${api_url_bca}/purchase-${api_version}/send`, {
      ...event,
    });
  }

  public getIdealIssuers(): Observable<IIssuerResponse> {
    const { api_url_bca, api_version } = this._config;
    return this._http.get<IIssuerResponse>(
      `${api_url_bca}/ideal-${api_version}/issuers`,
    );
  }

  public getAgreementPreview(
    receipt: fromReceipt.IState,
  ): Observable<IAgreementPreviewResponse> {
    const { api_url_bca, api_version } = this._config;
    const language = this._translate.currentLang;

    return this._http.post<IAgreementPreviewResponse>(
      `${api_url_bca}/previewofagreementdocument-${api_version}/previewofagreementdocument`,
      {
        ...receipt,
        language,
      },
    );
  }

  public getActionCode(actionCode: string): Observable<IActionCodeResponse> {
    const { api_url_bca, api_version } = this._config;
    return this._http.get<IActionCodeResponse>(
      `${api_url_bca}/actiecode-${api_version}/actiecode?code=${actionCode}`,
    );
  }

  public getCompanyCheck(kvkNumber: string): Observable<ICompanyCheckResponse> {
    const { api_url_bca, api_version } = this._config;
    return this._http.get<ICompanyCheckResponse>(
      `${api_url_bca}/company-check-${api_version}/company-check?kvknummer=${kvkNumber}`,
    );
  }

  public getMiddleNames(): Observable<IMiddleNamesResponse> {
    const { api_url_bca, api_version } = this._config;
    return this._http.get<IMiddleNamesResponse>(
      `${api_url_bca}/tussenvoegsel-${api_version}/tussenvoegsel`,
    );
  }

  public getCompanyProfile(
    kvkNumber: string,
  ): Observable<ICompanyProfileResponse> {
    const { api_url_bca, api_version } = this._config;
    return this._http.get<ICompanyProfileResponse>(
      `${api_url_bca}/kvk-${api_version}/profile?input=${kvkNumber}`,
    );
  }

  public getAddress(
    address: ISearchableAddress,
  ): Observable<IAddressResponse[]> {
    const { api_url_rio, api_key_rio } = this._config;
    const query = address.postcode + ` ` + address.houseNumber;
    return this._http.get<IAddressResponse[]>(
      `${api_url_rio}/locaties?q=${query}`,
      {
        headers: {
          "Ocp-Apim-Subscription-Key": api_key_rio,
        },
      },
    );
  }

  public validateImage(image: File): Observable<IImageValidationResponse[]> {
    const { maxNumberOfFacesInImage } = environment.userData;
    const { api_url_pasfoto } = this._config;

    const data = new FormData();
    data.append("file", image, image.name);

    const language = this._translate.currentLang;

    return this._http.post<IImageValidationResponse[]>(
      `${api_url_pasfoto}/validate?maxNumberOfFaces=${maxNumberOfFacesInImage}&canConstructFacesUsingProfile=true&language=${language}`,
      data,
    );
  }
}
